import React, { useState, useEffect, useRef } from "react";
import { db } from "../../firebaseConfig";
import ro from "/src/content/ro.json";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import ReCAPTCHA from "react-google-recaptcha";
import { toStorageDate } from "../../utils/date";

const ParticipCard = () => {
  const [formData, setFormData] = useState({
    tipTraseu: "MAI",
    dataTraseu: "",
    nume: "",
    email: "",
    telefon: "",
    varsta: "",
    inaltime: "",
    greutate: "",
  });

  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // tokenul captcha + referinta
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // Cheia ta reCAPTCHA din .env
  const RECAPTCHA_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  // aduce datele din stabilire_program
  useEffect(() => {
    const fetchDates = async () => {
      try {
        const tipUpper = formData.tipTraseu.toUpperCase();
        const q = query(
          collection(db, "stabilire_program"),
          where("tipProgram", "==", tipUpper)
        );
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const normalized = data.map((item) => ({
          ...item,
          data: toStorageDate(item.data) || item.data,
        }));
        setAvailableDates(normalized);
      } catch (err) {
        console.error("❌ Eroare la preluarea datelor:", err);
      }
    };

    fetchDates();
  }, [formData.tipTraseu]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.id]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const {
        nume,
        email,
        telefon,
        varsta,
        inaltime,
        greutate,
        tipTraseu,
        dataTraseu,
      } = formData;

      if (
        !nume ||
        !email ||
        !telefon ||
        !varsta ||
        !inaltime ||
        !greutate ||
        !dataTraseu
      ) {
        throw new Error(ro.particip.errors.required);
      }

      if (!captchaToken) {
        throw new Error(ro.particip.errors.captcha);
      }

      // 🔥 scriere Firestore compatibilă cu regulile tale
      await addDoc(collection(db, "inscrieri_traseu"), {
        ...formData,
        createdAt: serverTimestamp(),
        recaptchaVerified: true, // necesar pentru reguli
        recaptchaToken: captchaToken,
      });

      setMessage(ro.particip.success);

      setFormData({
        tipTraseu,
        dataTraseu: "",
        nume: "",
        email: "",
        telefon: "",
        varsta: "",
        inaltime: "",
        greutate: "",
      });

      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } catch (err) {
      console.error(err);
      setMessage(ro.particip.errorPrefix + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSliderChange = (direction) => {
    const options = ["MAI", "MAPN", "ISU", "ANP"];
    const currentIndex = options.indexOf(formData.tipTraseu);
    const nextIndex =
      direction === "next"
        ? (currentIndex + 1) % options.length
        : (currentIndex - 1 + options.length) % options.length;
    setFormData((prev) => ({
      ...prev,
      tipTraseu: options[nextIndex],
      dataTraseu: "",
    }));
  };

  const inputStyle = { color: "#000", backgroundColor: "#fff" };

  return (
    <section className="particip-section">
      <div className="particip-card">
        <h2 className="title">{ro.particip.title}</h2>

        <form className="form-grid" onSubmit={handleSubmit}>
          <div className="form-col">
            {/* SLIDER */}
            <div className="form-group">
              <label>{ro.particip.tipTraseuLabel}</label>
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  className="px-3 py-1 bg-neutral-800 text-white rounded-md"
                  onClick={() => handleSliderChange("prev")}
                >
                  ◀
                </button>
                <span className="font-semibold text-orange-500 text-lg w-20 text-center">
                  {formData.tipTraseu}
                </span>
                <button
                  type="button"
                  className="px-3 py-1 bg-neutral-800 text-white rounded-md"
                  onClick={() => handleSliderChange("next")}
                >
                  ▶
                </button>
              </div>
            </div>

            {/* SELECT DATA */}
            <div className="form-group">
              <label htmlFor="dataTraseu">{ro.particip.dataTraseuLabel}</label>
              <select
                id="dataTraseu"
                value={formData.dataTraseu}
                onChange={handleChange}
                required
                className="themed-select"
              >
                <option value="">
                  {availableDates.length
                    ? ro.particip.selectDate
                    : ro.particip.noSchedule}
                </option>
                {availableDates.map((d, i) => (
                  <option key={i} value={d.data}>
                    {`${d.data} – ${d.ora || ""} ${
                      d.locatie ? `(${d.locatie})` : ""
                    }`}
                  </option>
                ))}
              </select>
            </div>

            {/* INPUT URI */}
            {["nume", "varsta", "inaltime"].map((field) => (
              <div className="form-group" key={field}>
                <label htmlFor={field}>
                  {field === "nume"
                    ? ro.particip.labels.nume
                    : field === "varsta"
                    ? ro.particip.labels.varsta
                    : ro.particip.labels.inaltime}
                </label>
                <input
                  id={field}
                  type={field === "nume" ? "text" : "number"}
                  value={formData[field]}
                  onChange={handleChange}
                  placeholder={
                    field === "nume"
                      ? ro.particip.placeholders.nume
                      : field === "varsta"
                      ? ro.particip.placeholders.varsta
                      : ro.particip.placeholders.inaltime
                  }
                  required
                  style={inputStyle}
                />
              </div>
            ))}
          </div>

          <div className="form-col">
            {["email", "telefon", "greutate"].map((field) => (
              <div className="form-group" key={field}>
                <label htmlFor={field}>
                  {field === "email"
                    ? ro.particip.labels.email
                    : field === "telefon"
                    ? ro.particip.labels.telefon
                    : ro.particip.labels.greutate}
                </label>
                <input
                  id={field}
                  type={
                    field === "email"
                      ? "email"
                      : field === "telefon"
                      ? "tel"
                      : "number"
                  }
                  value={formData[field]}
                  onChange={handleChange}
                  placeholder={
                    field === "email"
                      ? ro.particip.placeholders.email
                      : field === "telefon"
                      ? ro.particip.placeholders.telefon
                      : ro.particip.placeholders.greutate
                  }
                  required
                  style={inputStyle}
                />
              </div>
            ))}

            {/* CAPTCHA */}
            <div className="form-group text-center pb-2">
              <label className="block mb-2 font-semibold">
                {ro.particip.captchaLabel}
              </label>
              <div className="flex justify-center">
                <ReCAPTCHA
                  sitekey={RECAPTCHA_KEY}
                  ref={recaptchaRef}
                  onChange={(token) => setCaptchaToken(token)}
                  theme={
                    document.documentElement.classList.contains("dark")
                      ? "dark"
                      : "light"
                  }
                  size="normal"
                />
              </div>
            </div>

            {/* BUTTON */}
            <div className="flex justify-center">
              <button
                type="submit"
                className="btn-orange"
                disabled={loading || !captchaToken}
              >
                {loading ? ro.particip.submitLoading : ro.particip.submit}
              </button>
            </div>
          </div>
        </form>

        {message && (
          <p
            className={`text-center mt-4 ${
              message.startsWith("✅") ? "text-green-500" : "text-red-500"
            }`}
          >
            {message}
          </p>
        )}
      </div>
    </section>
  );
};

export default ParticipCard;
